/**
 * Отправка с мгновенным появлением пузыря.
 *   1. в ленту сразу кладётся сообщение с пометкой «отправляется» (echo_id — наш номер);
 *   2. уходит POST; ответ сервера (или событие message.created, что придёт первым) заменяет его;
 *   3. при сбое пузырь остаётся с красной рамкой, «Повторить» шлёт то же ещё раз, «Убрать» удаляет.
 */
import type { QueryClient } from '@tanstack/react-query';
import { sendMessage, sendMessageForm } from '@/api/inbox';
import type { CwMessage } from '@/api/inboxTypes';
import { buildMessageForm, localAttachments } from './attachments';
import { patchAllLists, cwKeys } from './cwCache';
import {
  addLocal, emptyState, makeLocalMessage, markLocal, removeLocal, upsertServerMessage, type MessagesState,
} from './messageStore';

export function newEchoId(): string {
  const c = globalThis.crypto;
  const rnd = c && 'randomUUID' in c ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `saleus-${rnd}`;
}

export interface SendContext {
  qc: QueryClient;
  accountId: number;
  convId: number;
  myUserId: number | null;
  myName: string;
}

function update(ctx: SendContext, fn: (s: MessagesState) => MessagesState): void {
  const key = cwKeys.msgs(ctx.accountId, ctx.convId);
  ctx.qc.setQueryData<MessagesState>(key, (old) => fn(old ?? emptyState()));
}

function errorText(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Не удалось отправить';
}

/** Что уходит: текст или файлы. Файлы в кэше TanStack не хранятся (они не копируются) — держим здесь. */
interface FilesJob { files: File[]; isVoice: boolean; urls: string[] }
const fileJobs = new Map<string, FilesJob>();

function releaseJob(echoId: string): void {
  const job = fileJobs.get(echoId);
  if (!job) return;
  job.urls.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* не страшно */ } });
  fileJobs.delete(echoId);
}

async function post(ctx: SendContext, echoId: string, content: string, isPrivate: boolean): Promise<void> {
  try {
    const job = fileJobs.get(echoId);
    const sent: CwMessage = job
      ? await sendMessageForm(ctx.accountId, ctx.convId, buildMessageForm({
          content, isPrivate, echoId, files: job.files, isVoice: job.isVoice,
        }))
      : await sendMessage(ctx.accountId, ctx.convId, { content, isPrivate, echoId });
    update(ctx, (s) => upsertServerMessage(s, sent));
    // Если связь с событиями оборвана, список всё равно узнает о ходе переписки.
    patchAllLists(ctx.qc, ctx.accountId, { kind: 'message', convId: ctx.convId, message: sent }, ctx.myUserId);
    releaseJob(echoId);
  } catch (e) {
    update(ctx, (s) => markLocal(s, echoId, { local: 'failed', localError: errorText(e) }));
  }
}

/** Отправить новое. Возвращает echo_id (нужен для проверок). */
export function send(ctx: SendContext, content: string, isPrivate: boolean, now: () => number = () => Math.floor(Date.now() / 1000)): string {
  const echoId = newEchoId();
  const local = makeLocalMessage({
    echoId, conversationId: ctx.convId, content, isPrivate, userId: ctx.myUserId, userName: ctx.myName, now: now(),
  });
  update(ctx, (s) => addLocal(s, local));
  void post(ctx, echoId, content, isPrivate);
  return echoId;
}

export interface FilesInput {
  files: File[];
  /** Подпись к файлам (может быть пустой). */
  caption: string;
  isPrivate: boolean;
  /** Запись с микрофона: уходит с пометкой «голосовое». */
  isVoice?: boolean;
}

/**
 * Отправить файлы (и подпись) одним сообщением. Пузырь появляется сразу — с превью из памяти телефона;
 * «отправляется» держится, пока идёт загрузка; при сбое — «Повторить» шлёт те же файлы ещё раз.
 */
export function sendFiles(ctx: SendContext, input: FilesInput, now: () => number = () => Math.floor(Date.now() / 1000)): string {
  const echoId = newEchoId();
  const urls: string[] = [];
  const urlOf = (f: File): string => {
    const u = URL.createObjectURL(f);
    urls.push(u);
    return u;
  };
  fileJobs.set(echoId, { files: input.files, isVoice: !!input.isVoice, urls });
  const local = makeLocalMessage({
    echoId, conversationId: ctx.convId, content: input.caption.trim(), isPrivate: input.isPrivate,
    userId: ctx.myUserId, userName: ctx.myName, now: now(),
    attachments: localAttachments(input.files, urlOf),
  });
  update(ctx, (s) => addLocal(s, local));
  void post(ctx, echoId, input.caption.trim(), input.isPrivate);
  return echoId;
}

/** Повторить неудавшееся. */
export function retry(ctx: SendContext, echoId: string): void {
  const s = ctx.qc.getQueryData<MessagesState>(cwKeys.msgs(ctx.accountId, ctx.convId));
  const m = s?.messages.find((x) => x.local && x.echo_id === echoId);
  if (!m) return;
  update(ctx, (st) => markLocal(st, echoId, { local: 'sending' }));
  void post(ctx, echoId, m.content ?? '', m.private);
}

export function discard(ctx: SendContext, echoId: string): void {
  releaseJob(echoId);
  update(ctx, (s) => removeLocal(s, echoId));
}
